import { useEffect, useState } from "react";
import MidPopup from "../../hoc/MidPopup/MidPopup";
import { toast } from "react-toastify";
import {
  updateDailyComment,
  createDailyComment,
  getDailyComment,
} from "../../api/dailyComment/dailyComment"; // Import the create function
import { formatDate } from "../../shared/dateUtils";
import WeekDaySelector from "../../hoc/DatePicker/Datepicker";
import { addDays, subDays } from "date-fns";
import "./HandleDailyCommentPopup.scss";
import { formatDateToSwedish } from "../../shared/utility";

interface IHandleDailyCommentPopupProps {
  onClose: () => void;
  showPopup: boolean;
  onConfirm: () => void;
}

function HandleDailyCommentPopup(props: IHandleDailyCommentPopupProps) {
  const { onClose, showPopup, onConfirm } = props;
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [endDate, setEndDate] = useState<Date>(subDays(new Date(), -1));
  const [comments, setComments] = useState<
    { date: string; comment: string; isNew: boolean }[]
  >([]); // Added isNew flag
  const [initialComments, setInitialComments] = useState<
    { date: string; comment: string; isNew: boolean }[]
  >([]);

  const dailyCommentTypes = [
    { label: "Inomhus", value: "INDOOR" },
    { label: "Utomhus", value: "OUTDOOR" },
    { label: "Rehab", value: "REHAB" },
  ];
  const [indoorComments, setIndoorComments] = useState<
    { date: string; comment: string; isNew: boolean }[]
  >([]);
  const [outdoorComments, setOutdoorComments] = useState<
    { date: string; comment: string; isNew: boolean }[]
  >([]);
  const [rehabComments, setRehabComments] = useState<
    { date: string; comment: string; isNew: boolean }[]
  >([]);
  const [initialIndoorComments, setInitialIndoorComments] = useState<
    { date: string; comment: string; isNew: boolean }[]
  >([]);
  const [initialOutdoorComments, setInitialOutdoorComments] = useState<
    { date: string; comment: string; isNew: boolean }[]
  >([]);
  const [initialRehabComments, setInitialRehabComments] = useState<
    { date: string; comment: string; isNew: boolean }[]
  >([]);

  const [isTodayUpdated, setIsTodayUpdated] = useState<boolean>(false);

  // Reset popup when closed
  const handleClose = () => {
    setStartDate(new Date());
    setEndDate(subDays(new Date(), -1));
    setComments([]);
    onClose();
    if (isTodayUpdated) {
      onConfirm();
    }
  };

  const handleSetStartDate = (date: Date | null) => {
    if (date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (date < today) {
        toast.error("Välj ett aktuellt datum");
        return;
      }
      setStartDate(date);

      if (endDate <= date) {
        setEndDate(addDays(date, 1));
      }
    }
  };

  const handleSetEndDate = (date: Date | null) => {
    if (date) {
      if (date <= startDate) {
        toast.error("Slutdatum måste vara minst en dag efter startdatum");
        setEndDate(addDays(startDate, 1));
      } else {
        setEndDate(date);
      }
    }
  };


  const handleFetchCommentsAllTypes = async () => {
    try {
      const dates = [];
      let currentDate = new Date(startDate);
      const fetchedIndoorComments: {
        date: string;
        comment: string;
        isNew: boolean;
      }[] = [];
      const fetchedOutdoorComments: {
        date: string;
        comment: string;
        isNew: boolean;
      }[] = [];
      const fetchedRehabComments: {
        date: string;
        comment: string;
        isNew: boolean;
      }[] = [];
      while (currentDate <= endDate) {
        const formattedDate = formatDate(currentDate, "yyyy-MM-dd");
        dates.push(formattedDate);
        currentDate = addDays(currentDate, 1);
      }
      // Fetch comments for each date for all types
      const promises = dates.map(async (date) => {
        const indoorResponse = await getDailyComment(date, "INDOOR");
        const outdoorResponse = await getDailyComment(date, "OUTDOOR");
        const rehabResponse = await getDailyComment(date, "REHAB");
        // Set isNew flag to true if no comment exists
        const indoorIsNew = indoorResponse.comment === "No comment";
        const outdoorIsNew = outdoorResponse.comment === "No comment";
        const rehabIsNew = rehabResponse.comment === "No comment";
        fetchedIndoorComments.push({
          date,
          comment: indoorResponse.comment || "",
          isNew: indoorIsNew,
        });
        fetchedOutdoorComments.push({
          date,
          comment: outdoorResponse.comment || "",
          isNew: outdoorIsNew,
        });
        fetchedRehabComments.push({
          date,
          comment: rehabResponse.comment || "",
          isNew: rehabIsNew,
        });
      });
      await Promise.all(promises);
      const indoorCommentsSorted = fetchedIndoorComments.sort((a, b) => {
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      });
      const outdoorCommentsSorted = fetchedOutdoorComments.sort((a, b) => {
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      });
      const rehabCommentsSorted = fetchedRehabComments.sort((a, b) => {
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      });
      setIndoorComments(indoorCommentsSorted);
      setOutdoorComments(outdoorCommentsSorted);
      setRehabComments(rehabCommentsSorted);
      setInitialIndoorComments(
        JSON.parse(JSON.stringify(indoorCommentsSorted))
      );
      setInitialOutdoorComments(
        JSON.parse(JSON.stringify(outdoorCommentsSorted))
      );
      setInitialRehabComments(JSON.parse(JSON.stringify(rehabCommentsSorted)));
      if (
        fetchedIndoorComments.length === 0 &&
        fetchedOutdoorComments.length === 0 &&
        fetchedRehabComments.length === 0
      ) {
        toast.warning("Inga kommentarer hittades");
      }
    } catch (error: any) {
      toast.error("Något gick fel vid hämtning av kommentarer");
    }
  };

  const handleSaveComment = async (
    date: string,
    comment: string,
    isNew: boolean,
    dailyCommentType: "INDOOR" | "OUTDOOR" | "REHAB"
  ) => {
    try {
      let response;
      if (isNew) {
        // Create new comment if the flag indicates it's new
        response = await createDailyComment(date, comment, dailyCommentType);
      } else {
        // Update existing comment
        response = await updateDailyComment(date, comment, dailyCommentType);
      }

      if (response && "message" in response) {
        toast.error(response.message);
      } else {
        switch (dailyCommentType) {
          case "INDOOR":
            setIndoorComments(
              indoorComments.map((c) => {
                if (c.date === date) {
                  return { ...c, comment, isNew: false }; // Update comment and set isNew to false
                }
                return c;
              })
            );
            setInitialIndoorComments(
              initialIndoorComments.map((c) => {
                if (c.date === date) {
                  return { ...c, comment, isNew: false }; // Also update initialComments
                }
                return c;
              })
            );
            break;
          case "OUTDOOR":
            setOutdoorComments(
              outdoorComments.map((c) => {
                if (c.date === date) {
                  return { ...c, comment, isNew: false }; // Update comment and set isNew to false
                }
                return c;
              })
            );
            setInitialOutdoorComments(
              initialOutdoorComments.map((c) => {
                if (c.date === date) {
                  return { ...c, comment, isNew: false }; // Also update initialComments
                }
                return c;
              })
            );
            break;
          case "REHAB":
            setRehabComments(
              rehabComments.map((c) => {
                if (c.date === date) {
                  return { ...c, comment, isNew: false }; // Update comment and set isNew to false
                }
                return c;
              })
            );
            setInitialRehabComments(
              initialRehabComments.map((c) => {
                if (c.date === date) {
                  return { ...c, comment, isNew: false }; // Also update initialComments
                }
                return c;
              })
            );
            break;
          default:
            break;
        }

        const today = formatDate(new Date(), "yyyy-MM-dd");
        if (date === today) {
          setIsTodayUpdated(true);
        }
        toast.success(`Kommentar för ${dailyCommentType} den ${date} har uppdaterats`);
      }
    } catch (error: any) {
      toast.error(
        error.errors
          ? error.errors[0].message
          : "Ett fel uppstod vid uppdatering"
      );
    }
  };

  // Handle comment change in the list
  const handleCommentChange = (
    index: number,
    newComment: string,
    type: "INDOOR" | "OUTDOOR" | "REHAB"
  ) => {
    switch (type) {
      case "INDOOR":
        setIndoorComments((prev) =>
          prev.map((c, i) => (i === index ? { ...c, comment: newComment } : c))
        );
        break;
      case "OUTDOOR":
        setOutdoorComments((prev) =>
          prev.map((c, i) => (i === index ? { ...c, comment: newComment } : c))
        );
        break;
      case "REHAB":
        setRehabComments((prev) =>
          prev.map((c, i) => (i === index ? { ...c, comment: newComment } : c))
        );
        break;
      default:
        break;
    }
    // const updatedComments = [...comments];
    // updatedComments[index].comment = newComment;
    // setComments(updatedComments);
  };

  return (
    <MidPopup
      onClose={handleClose}
      showPopup={showPopup}
      leftTopElement={<span className="text-m">Hantera kommentarer</span>}
      onConfirm={() => {}}
      width="70vw"
    >
      <div className="comment-popup-wrapper">
        <div className="comment-button-input-wrapper">
          <div className="comment-input-wrapper"></div>

          <div className="handle-comments-date-wrapper">
            <div className="handle-comments-date-picker">
              <span className="text-m">Från</span>
              <WeekDaySelector
                includeWeekdays={false}
                value={startDate}
                onChange={handleSetStartDate}
                isSmall
                includePicker
              />
            </div>
            <div className="handle-comments-date-picker">
              <span className="text-m">Till</span>
              <WeekDaySelector
                includeWeekdays={false}
                value={endDate}
                onChange={handleSetEndDate}
                isSmall
                includePicker
              />
            </div>
            <button
              className="comment-button text-m"
              onClick={handleFetchCommentsAllTypes}
            >
              Hämta kommentarer
            </button>
          </div>
          <div className="comments-lists-grid">
            {/* Display fetched comments */}
            {indoorComments.length > 0 && (
              <div className="comments-list">
                <p className="text-m toggle-button">Innomhus</p>
                {indoorComments.map(({ date, comment, isNew }, index) => {
                  const { weekday, dateAndMonth } = formatDateToSwedish(
                    new Date(date)
                  );
                  const value = comment === "No comment" ? "" : comment;
                  return (
                    <div key={index} className="comment-item text-m">
                      <div>
                        <span>
                          {weekday}, {dateAndMonth}:
                        </span>
                      </div>
                      <div className="comment-item-input-btn-wrapper">
                        <input
                          type="text"
                          value={value}
                          onChange={(e) =>
                            handleCommentChange(index, e.target.value, "INDOOR")
                          }
                        />
                        <button
                          disabled={
                            indoorComments[index].comment ===
                            initialIndoorComments[index].comment
                          }
                          onClick={() =>
                            handleSaveComment(
                              date,
                              indoorComments[index].comment,
                              isNew,
                              "INDOOR"
                            )
                          }
                        >
                          {isNew ? "Skapa" : "Uppdatera"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {outdoorComments.length > 0 && (
              <div className="comments-list">
                <div className="text-m toggle-button">Utomhus</div>
                {outdoorComments.map(({ date, comment, isNew }, index) => {
                  const { weekday, dateAndMonth } = formatDateToSwedish(
                    new Date(date)
                  );
                  const value = comment === "No comment" ? "" : comment;
                  return (
                    <div key={index} className="comment-item text-m">
                      <div>
                        <span>
                          {weekday}, {dateAndMonth}:
                        </span>
                      </div>
                      <div className="comment-item-input-btn-wrapper">
                        <input
                          type="text"
                          value={value}
                          onChange={(e) =>
                            handleCommentChange(
                              index,
                              e.target.value,
                              "OUTDOOR"
                            )
                          }
                        />
                        <button
                          disabled={
                            outdoorComments[index].comment ===
                            initialOutdoorComments[index].comment
                          }
                          onClick={() =>
                            handleSaveComment(
                              date,
                              outdoorComments[index].comment,
                              isNew,
                              "OUTDOOR"
                            )
                          }
                        >
                          {isNew ? "Skapa" : "Uppdatera"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {rehabComments.length > 0 && (
              <div className="comments-list">
                <div className="text-m toggle-button">Rehab-bad</div>
                {rehabComments.map(({ date, comment, isNew }, index) => {
                  const { weekday, dateAndMonth } = formatDateToSwedish(
                    new Date(date)
                  );
                  const value = comment === "No comment" ? "" : comment;
                  return (
                    <div key={index} className="comment-item text-m">
                      <div>
                        <span>
                          {weekday}, {dateAndMonth}:
                        </span>
                      </div>
                      <div className="comment-item-input-btn-wrapper">
                        <input
                          type="text"
                          value={value}
                          onChange={(e) =>
                            handleCommentChange(index, e.target.value, "REHAB")
                          }
                        />
                        <button
                          disabled={
                            rehabComments[index].comment ===
                            initialRehabComments[index].comment
                          }
                          onClick={() =>
                            handleSaveComment(
                              date,
                              rehabComments[index].comment,
                              isNew,
                              "REHAB"
                            )
                          }
                        >
                          {isNew ? "Skapa" : "Uppdatera"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </MidPopup>
  );
}

export default HandleDailyCommentPopup;
